import { useCallback, useEffect, useRef, useState } from 'react'
import { ReactFlowProvider } from '@xyflow/react'
import MapView, { type MapMode } from './components/MapView'
import Drawer from './components/Drawer'
import { ALGO_INDEX, PROBLEMS } from './data/models'
import { BEGINNER } from './data/beginner'
import type { Algorithm, RunResult, RunState } from './types'
import { type RunHandle } from './lib/mockRun'
import { runPipelineRemote } from './lib/realRun'
import { fireConfetti } from './lib/confetti'

// Action-first UI: the map IS the home. Click a question → drawer with
// sliders + RUN → challenge → solved ✓ on the map. The pre-redesign tree UI
// lives on the `master` branch.

const EMPTY_RUN: RunState = { status: 'idle', stageStatus: {}, logs: [], result: null }

const LS_RUNS = 'mlmate.runs'
const LS_SOLVED = 'mlmate.solved'

const MIN_WB = 220 // px — smallest useful drawer
// 40% drawer / 60% map — the map is the main surface (Francis, 2026-07-06)
const defaultWbHeight = () => Math.round(window.innerHeight * 0.4)
const maxWbHeight = () => Math.round(window.innerHeight * 0.86)

function loadSolved(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(LS_SOLVED) ?? '{}')
  } catch {
    return {}
  }
}

function defaultInputs(algo: Algorithm): Record<string, number | string> {
  const out: Record<string, number | string> = {}
  for (const f of algo.inputs) out[f.name] = f.default
  return out
}

function bigLabel(result: RunResult): string {
  if (result.kind === 'classification' && result.probability !== undefined)
    return Math.round(result.probability * 100) + '%'
  return result.headline
}

// open on problem 1 with its first model selected and the Problem tab showing,
// so the first screen demonstrates the whole idea instead of an empty map
const INITIAL_PROBLEM = 'churn'
const INITIAL_ALGO = 'churn-xgb'

export default function App() {
  const [mapMode, setMapMode] = useState<MapMode>('problems')
  const [expandedFamily, setExpandedFamily] = useState<string | null>('XGBoost')
  const [expandedProblemId, setExpandedProblemId] = useState<string | null>(INITIAL_PROBLEM)
  const [selectedProblemId, setSelectedProblemId] = useState<string | null>(INITIAL_PROBLEM)
  const [selectedAlgoId, setSelectedAlgoId] = useState<string | null>(INITIAL_ALGO)
  const [inputs, setInputs] = useState<Record<string, number | string>>(() =>
    defaultInputs(ALGO_INDEX[INITIAL_ALGO].algo),
  )
  const [run, setRun] = useState<RunState>(EMPTY_RUN)
  const [hasRun, setHasRun] = useState(false)
  const [justWon, setJustWon] = useState(false)
  const [runs, setRuns] = useState<number>(() => Number(localStorage.getItem(LS_RUNS)) || 0)
  const [solved, setSolved] = useState<Record<string, string>>(loadSolved)
  const [toast, setToast] = useState<string | null>(null)
  const [wbHeight, setWbHeight] = useState<number>(defaultWbHeight)

  const onDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    const move = (ev: PointerEvent) => {
      const h = window.innerHeight - ev.clientY
      setWbHeight(Math.min(Math.max(h, MIN_WB), maxWbHeight()))
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'row-resize'
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }, [])

  const handleRef = useRef<RunHandle | null>(null)
  const hasRunRef = useRef(false)
  const solvedRef = useRef(solved)
  solvedRef.current = solved
  const toastT = useRef(0)

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    window.clearTimeout(toastT.current)
    toastT.current = window.setTimeout(() => setToast(null), 2800)
  }, [])

  useEffect(() => () => window.clearTimeout(toastT.current), [])

  const stopRun = useCallback(() => {
    handleRef.current?.cancel()
    handleRef.current = null
  }, [])

  const onModeChange = useCallback((mode: MapMode) => {
    setMapMode(mode)
  }, [])

  // models mode: clicking a family expands/collapses the problems it solves
  const onFamilyClick = useCallback((name: string) => {
    setExpandedFamily((prev) => (prev === name ? null : name))
  }, [])

  // clicking a problem expands/collapses its models on the map
  const onProblemClick = useCallback(
    (id: string) => {
      stopRun()
      setExpandedProblemId((prev) => (prev === id ? null : id))
      setSelectedProblemId(null)
      setSelectedAlgoId(null)
      setRun(EMPTY_RUN)
    },
    [stopRun],
  )

  // clicking a model opens the story drawer
  const onAlgoClick = useCallback(
    (id: string) => {
      stopRun()
      const entry = ALGO_INDEX[id]
      if (!entry) return
      setSelectedProblemId(entry.problem.id)
      setSelectedAlgoId(id)
      setInputs(defaultInputs(entry.algo))
      setRun(EMPTY_RUN)
      setHasRun(false)
      hasRunRef.current = false
      setJustWon(false)
    },
    [stopRun],
  )

  const onClose = useCallback(() => {
    stopRun()
    setSelectedProblemId(null)
    setSelectedAlgoId(null)
    setRun(EMPTY_RUN)
  }, [stopRun])

  const onSwitchAlgo = useCallback(
    (id: string) => {
      stopRun()
      setSelectedAlgoId(id)
      setRun(EMPTY_RUN)
    },
    [stopRun],
  )

  const onInputChange = useCallback((name: string, value: number | string) => {
    setInputs((prev) => ({ ...prev, [name]: value }))
  }, [])

  const onRun = useCallback(() => {
    if (!selectedProblemId || !selectedAlgoId) return
    const { algo } = ALGO_INDEX[selectedAlgoId]
    const cfg = BEGINNER[selectedProblemId]
    const problemId = selectedProblemId
    stopRun()
    setJustWon(false)
    setRun({ status: 'running', stageStatus: {}, logs: [], result: null })
    handleRef.current = runPipelineRemote(algo, inputs, {
      onStageStatus: (stageId, status) =>
        setRun((prev) => ({ ...prev, stageStatus: { ...prev.stageStatus, [stageId]: status } })),
      onLog: (line) => setRun((prev) => ({ ...prev, logs: [...prev.logs, line] })),
      onResult: (result) => {
        setRun((prev) => ({ ...prev, result }))
        // challenge is revealed by the first run and can be won from the second
        if (hasRunRef.current && !(problemId in solvedRef.current) && cfg.challenge.check(result)) {
          const next = { ...solvedRef.current, [problemId]: bigLabel(result) }
          solvedRef.current = next
          setSolved(next)
          localStorage.setItem(LS_SOLVED, JSON.stringify(next))
          setJustWon(true)
          fireConfetti()
        }
      },
      onDone: () => {
        setRun((prev) => ({ ...prev, status: 'done' }))
        setHasRun(true)
        hasRunRef.current = true
        setRuns((prev) => {
          const next = prev + 1
          localStorage.setItem(LS_RUNS, String(next))
          if (next === 3) showToast('🏅 3 runs — Pattern Spotter unlocked')
          return next
        })
      },
    })
  }, [selectedProblemId, selectedAlgoId, inputs, stopRun, showToast])

  const problem = selectedProblemId ? PROBLEMS.find((p) => p.id === selectedProblemId) : null
  const algo = selectedAlgoId ? ALGO_INDEX[selectedAlgoId]?.algo : null

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <header className="flex shrink-0 items-center justify-between border-b border-ink-700 bg-ink-900/70 px-5 py-2.5 backdrop-blur">
        <div className="flex items-baseline gap-3">
          <span className="bg-gradient-to-r from-accent-400 to-fuchsia-400 bg-clip-text text-xl font-bold tracking-tight text-transparent">
            MLMate
          </span>
          <span className="hidden text-xs text-mut sm:inline">watch it learn</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] font-bold">
          <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-amber-300">
            ⚡ {runs} {runs === 1 ? 'run' : 'runs'}
          </span>
          <span className="rounded-full border border-brand-500/40 bg-brand-500/10 px-3 py-1 text-brand-300">
            🏅 {Object.keys(solved).length} / {PROBLEMS.length} solved
          </span>
        </div>
      </header>

      {/* map */}
      <div className="relative min-h-0 flex-1">
        <ReactFlowProvider>
          <MapView
            mode={mapMode}
            expandedProblemId={expandedProblemId}
            expandedFamily={expandedFamily}
            selectedAlgoId={selectedAlgoId}
            solved={solved}
            onProblemClick={onProblemClick}
            onFamilyClick={onFamilyClick}
            onAlgoClick={onAlgoClick}
            onModeChange={onModeChange}
          />
        </ReactFlowProvider>
      </div>

      {/* drawer */}
      {problem && algo && (
        <Drawer
          problem={problem}
          algo={algo}
          cfg={BEGINNER[problem.id]}
          inputs={inputs}
          run={run}
          hasRun={hasRun}
          isSolved={problem.id in solved && !justWon}
          justWon={justWon}
          height={wbHeight}
          onDragStart={onDragStart}
          onSwitchAlgo={onSwitchAlgo}
          onInputChange={onInputChange}
          onRun={onRun}
          onClose={onClose}
        />
      )}

      {/* toast */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 animate-popin rounded-xl border border-accent-500 bg-ink-800 px-5 py-2.5 text-[13px] font-semibold shadow-glow-accent">
          {toast}
        </div>
      )}
    </div>
  )
}
